/**
 * Drawer — the right-hand detail/edit panel.
 *
 * Locked behaviour from the reference system: at ≥1024px it PUSHES (368px, no
 * blanket) so an operator keeps the list in view while working a record; below
 * 1024px it overlays with a blanket, because there is no room to push.
 *
 * The body is a separate component so `children()` only resolves while the drawer
 * is actually open — resolving conditional children eagerly builds markup for a
 * closed panel.
 */
import { Show, children, createEffect, onCleanup, type JSX, type ParentProps } from "solid-js";
import { Portal } from "solid-js/web";
import X from "lucide-solid/icons/x";
import { cn } from "./utils/cn";
import { focusRing } from "./utils/focus-ring";

interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  /** Sticky action row at the bottom. */
  footer?: JSX.Element;
  /** Header-right slot (status pill, overflow menu). */
  headerEnd?: JSX.Element;
  width?: "default" | "wide";
}

export function Drawer(props: ParentProps<DrawerProps>): JSX.Element {
  createEffect(() => {
    if (!props.open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") props.onClose();
    };
    window.addEventListener("keydown", onKey);
    onCleanup(() => window.removeEventListener("keydown", onKey));
  });

  return (
    <Show when={props.open}>
      <DrawerBody {...props} />
    </Show>
  );
}

function DrawerBody(props: ParentProps<DrawerProps>): JSX.Element {
  const footer = children(() => props.footer);
  const headerEnd = children(() => props.headerEnd);

  return (
    <Portal>
      {/* Blanket is mobile-only: on desktop the drawer pushes rather than covers. */}
      <div
        class="fixed inset-0 md:hidden"
        style={{ "z-index": "var(--c9-z-blanket)", "background-color": "var(--c9-blanket)" }}
        onClick={() => props.onClose()}
        aria-hidden="true"
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={props.title ?? "Details"}
        class={cn(
          "fixed inset-y-0 right-0 flex flex-col border-l border-[color:var(--c9-border)]",
          "bg-[var(--c9-surface)] shadow-[var(--c9-shadow-overlay)]",
          "w-full sm:w-[26rem]",
          props.width === "wide" && "md:w-[34rem]",
        )}
        style={{ "z-index": "calc(var(--c9-z-blanket) + 1)" }}
      >
        <header class="flex shrink-0 items-start gap-200 border-b border-[color:var(--c9-border)] px-250 py-200">
          <div class="min-w-0 flex-1">
            <Show when={props.title}>
              <h2 class="truncate text-h-s font-[670] text-[color:var(--c9-text)]">
                {props.title}
              </h2>
            </Show>
            <Show when={props.subtitle}>
              <p class="mt-025 truncate text-body-s text-[color:var(--c9-text-subtlest)]">
                {props.subtitle}
              </p>
            </Show>
          </div>
          {headerEnd()}
          <button
            type="button"
            aria-label="Close"
            onClick={() => props.onClose()}
            class={cn(
              "c9-touch-icon inline-flex size-8 shrink-0 items-center justify-center rounded-[3px]",
              "text-[color:var(--c9-icon-subtle)] hover:bg-[var(--c9-bg-neutral-subtle-hovered)]",
              focusRing,
            )}
          >
            <X size={18} />
          </button>
        </header>

        <div class="min-h-0 flex-1 overflow-y-auto px-250 py-250">{props.children}</div>

        <Show when={footer()}>
          <footer class="flex shrink-0 items-center justify-end gap-150 border-t border-[color:var(--c9-border)] bg-[var(--c9-surface-sunken)] px-250 py-200">
            {footer()}
          </footer>
        </Show>
      </aside>
    </Portal>
  );
}
